import React, { lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { BrandingProvider } from "./contexts/BrandingContext";
import { OrganizationProvider } from "./contexts/OrganizationContext";
import { FeatureFlagsProvider } from "./contexts/FeatureFlagsContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import ErrorBoundary from "./components/ErrorBoundary";

// Robust Lazy Loading with Chunk Failure Auto-Recovery
const lazyWithRetry = (componentImport) =>
  lazy(async () => {
    try {
      return await componentImport();
    } catch (error) {
      console.warn('Chunk load error detected, attempting reload...', error);
      const reloadKey = 'chunk_reload_' + window.location.pathname;
      const hasReloaded = sessionStorage.getItem(reloadKey);
      if (!hasReloaded) {
        sessionStorage.setItem(reloadKey, 'true');
        window.location.reload();
        return new Promise(() => {});
      }
      throw error;
    }
  });

// Lazy-loaded Pages
const Login = lazyWithRetry(() => import("./pages/Login"));
const ForgotPassword = lazyWithRetry(() => import("./pages/ForgotPassword.jsx"));
const ResetPassword = lazyWithRetry(() => import("./pages/ResetPassword.jsx"));
const AcceptInvitation = lazyWithRetry(() => import("./pages/AcceptInvitation"));
const Dashboard = lazyWithRetry(() => import("./pages/Dashboard"));
const Owners = lazyWithRetry(() => import("./pages/Owners"));
const OwnerDetail = lazyWithRetry(() => import("./pages/OwnerDetail"));
const Apartments = lazyWithRetry(() => import("./pages/Apartments"));
const ApartmentDetail = lazyWithRetry(() => import("./pages/ApartmentDetail"));
const Tenants = lazyWithRetry(() => import("./pages/Tenants"));
const Contracts = lazyWithRetry(() => import("./pages/Contracts"));
const Payments = lazyWithRetry(() => import("./pages/Payments"));
const PaymentOrders = lazyWithRetry(() => import("./pages/PaymentOrders"));
const BankTransactions = lazyWithRetry(() => import("./pages/BankTransactions"));
const Banking = lazyWithRetry(() => import("./pages/Banking"));
const FinanceOverview = lazyWithRetry(() => import("./pages/FinanceOverview"));
const Ledger = lazyWithRetry(() => import("./pages/Ledger"));
const Reminders = lazyWithRetry(() => import("./pages/Reminders"));
const Tasks = lazyWithRetry(() => import("./pages/Tasks"));
const Tickets = lazyWithRetry(() => import("./pages/Tickets"));
const OwnerImport = lazyWithRetry(() => import("./pages/OwnerImport"));
const Migration = lazyWithRetry(() => import("./pages/Migration"));
const Contacts = lazyWithRetry(() => import("./pages/Contacts"));
const Documents = lazyWithRetry(() => import("./pages/Documents"));
const SentDocuments = lazyWithRetry(() => import("./pages/SentDocuments"));
const Settings = lazyWithRetry(() => import("./pages/Settings"));
const OperatingCosts = lazyWithRetry(() => import("./pages/OperatingCosts"));
const Admin = lazyWithRetry(() => import("./pages/Admin"));
const Reports = lazyWithRetry(() => import("./pages/Reports"));
const Administration = lazyWithRetry(() => import("./pages/Administration"));
const IngestionPreview = lazyWithRetry(() => import("./pages/IngestionPreview.jsx"));
const MieterhoehungProposals = lazyWithRetry(() => import("./pages/MieterhoehungProposals.jsx"));
const Pricing = lazyWithRetry(() => import("./pages/Pricing.jsx"));

const LocationAdminPage = lazyWithRetry(() => import("./pages/LocationAdmin"));
const RentIncreasePage = lazyWithRetry(() => import("./pages/RentIncreasePage"));
const Templates = lazyWithRetry(() => import("./pages/Templates"));
const TemplateEditor = lazyWithRetry(() => import("./pages/TemplateEditor"));
const Inbox = lazyWithRetry(() => import("./pages/Inbox"));
const Sales = lazyWithRetry(() => import("./pages/Sales"));
const SalesListingForm = lazyWithRetry(() => import("./pages/SalesListingForm"));
const SalesListingDetail = lazyWithRetry(() => import("./pages/SalesListingDetail"));
const OwnerPortalDashboard = lazyWithRetry(() => import("./pages/OwnerPortalDashboard"));
const OwnerPortalApartment = lazyWithRetry(() => import("./pages/OwnerPortalApartment"));
const OwnerPortalAcceptInvitation = lazyWithRetry(() => import("./pages/OwnerPortalAcceptInvitation"));
const OwnerPortalAdmin = lazyWithRetry(() => import("./pages/OwnerPortalAdmin"));
const Invoices = lazyWithRetry(() => import("./pages/Invoices"));
const InvoiceEditor = lazyWithRetry(() => import("./pages/InvoiceEditor"));
const InvoiceDetail = lazyWithRetry(() => import("./pages/InvoiceDetail"));
const InvoiceSettings = lazyWithRetry(() => import("./pages/InvoiceSettings"));

const GuestListingsPage = lazyWithRetry(() => import("./pages/GuestListingsPage"));
const GuestListingDetailPage = lazyWithRetry(() => import("./pages/GuestListingDetailPage"));
const Impressum = lazyWithRetry(() => import("./pages/Impressum"));
const Datenschutz = lazyWithRetry(() => import("./pages/Datenschutz"));

// Onboarding Lazy Pages
const OnboardingDashboard = lazyWithRetry(() => import("./pages/onboarding/OnboardingDashboard"));
const OnboardingWizardPage = lazyWithRetry(() => import("./pages/onboarding/OnboardingWizardPage"));
const OnboardingDetail = lazyWithRetry(() => import("./pages/onboarding/OnboardingDetail"));
const OnboardingTemplates = lazyWithRetry(() => import("./pages/onboarding/OnboardingTemplates"));

// Non-lazy global modal
import UpgradeModal from "./components/UpgradeModal";

const PageLoader = () => (
  <div className="min-h-[60vh] flex items-center justify-center bg-transparent">
    <div className="text-center">
      <div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
      <p className="text-xs text-muted-foreground font-medium">Laden...</p>
    </div>
  </div>
);
// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return <PageLoader />;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Centrally block viewer role from accessing non-sales/settings routes
  if (user?.role === 'viewer') {
    const allowedPaths = ['/sales', '/settings'];
    const currentPath = window.location.pathname;
    const isAllowed = allowedPaths.some(p => currentPath.startsWith(p)) || currentPath === '/';
    if (!isAllowed) {
      return <Navigate to="/sales" replace />;
    }
  }
  
  if (user?.role === 'owner_portal') {
    return <Navigate to="/owner-portal" replace />;
  }

  return children;
};

// Public Route Component (redirects if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return <PageLoader />;
  }
  
  if (isAuthenticated) {
    return <Navigate to={user?.role === 'owner_portal' ? "/owner-portal" : "/"} replace />;
  }

  return children;
};

// Owner portal route: only external owner accounts may access it.
const PortalProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-muted-foreground">Laden...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== 'owner_portal') {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Redirects viewer role to /sales
const DashboardRedirect = () => {
  const { user } = useAuth();
  if (user?.role === 'viewer') {
    return <Navigate to="/sales" replace />;
  }
  return <Dashboard />;
};

function AppRoutes() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
        <Route path="/reset-password" element={<PublicRoute><ResetPassword /></PublicRoute>} />
        <Route path="/accept-invitation/:token" element={<PublicRoute><AcceptInvitation /></PublicRoute>} />
        
        {/* Guest Sales Portal Public Routes */}
        <Route path="/guest/:token" element={<GuestListingsPage />} />
        <Route path="/guest/:token/listings/:listingId" element={<GuestListingDetailPage />} />
        
        {/* Protected Routes */}
        <Route path="/" element={<ProtectedRoute><DashboardRedirect /></ProtectedRoute>} />
        <Route path="/owners" element={<ProtectedRoute><Navigate to="/contacts/owners" replace /></ProtectedRoute>} />
        <Route path="/owners/:id" element={<ProtectedRoute><OwnerDetail /></ProtectedRoute>} />
        <Route path="/apartments" element={<ProtectedRoute><Apartments /></ProtectedRoute>} />
        <Route path="/apartments/:id" element={<ProtectedRoute><ApartmentDetail /></ProtectedRoute>} />
        <Route path="/tenants" element={<ProtectedRoute><Navigate to="/contacts/tenants" replace /></ProtectedRoute>} />
        <Route path="/contracts" element={<ProtectedRoute><Contracts /></ProtectedRoute>} />
        <Route path="/payments" element={<ProtectedRoute><Payments /></ProtectedRoute>} />
        <Route path="/payment-orders" element={<ProtectedRoute><PaymentOrders /></ProtectedRoute>} />
        <Route path="/bank-transactions" element={<ProtectedRoute><BankTransactions /></ProtectedRoute>} />
        <Route path="/banking" element={<ProtectedRoute><Banking /></ProtectedRoute>} />
        <Route path="/finance" element={<ProtectedRoute><FinanceOverview /></ProtectedRoute>} />
        <Route path="/ledger" element={<ProtectedRoute><Ledger /></ProtectedRoute>} />
        <Route path="/reminders" element={<ProtectedRoute><Reminders /></ProtectedRoute>} />
        <Route path="/operating-costs" element={<ProtectedRoute><OperatingCosts /></ProtectedRoute>} />
        <Route path="/tasks" element={<ProtectedRoute><Tasks /></ProtectedRoute>} />
        <Route path="/tickets" element={<ProtectedRoute><Tickets /></ProtectedRoute>} />
        <Route path="/migration" element={<ProtectedRoute><Navigate to="/settings" replace /></ProtectedRoute>} />
        <Route path="/contacts" element={<ProtectedRoute><Contacts /></ProtectedRoute>} />
        <Route path="/contacts/:tab" element={<ProtectedRoute><Contacts /></ProtectedRoute>} />
        <Route path="/service-providers" element={<ProtectedRoute><Navigate to="/contacts/service-providers" replace /></ProtectedRoute>} />
        <Route path="/hausverwaltungen" element={<ProtectedRoute><Navigate to="/contacts/hausverwaltungen" replace /></ProtectedRoute>} />
        <Route path="/owners/import" element={<ProtectedRoute><OwnerImport /></ProtectedRoute>} />
        <Route path="/owners/portal-access" element={<ProtectedRoute><OwnerPortalAdmin /></ProtectedRoute>} />
        <Route path="/documents" element={<ProtectedRoute><Documents /></ProtectedRoute>} />
        <Route path="/templates" element={<ProtectedRoute><Navigate to="/documents?tab=vorlagen" replace /></ProtectedRoute>} />
        <Route path="/templates/:id/edit" element={<ProtectedRoute><TemplateEditor /></ProtectedRoute>} />
        <Route path="/templates/new" element={<ProtectedRoute><TemplateEditor /></ProtectedRoute>} />
        <Route path="/sent-documents" element={<ProtectedRoute><Navigate to="/documents?tab=gesendet" replace /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="/settings/:section" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
        <Route path="/admin/access" element={<ProtectedRoute><Administration /></ProtectedRoute>} />
        <Route path="/admin/documents/ingestion-preview" element={<ProtectedRoute><IngestionPreview /></ProtectedRoute>} />
        <Route path="/admin/mieterhoehung/proposals" element={<ProtectedRoute><MieterhoehungProposals /></ProtectedRoute>} />

        <Route path="/admin/locations" element={<ProtectedRoute><LocationAdminPage /></ProtectedRoute>} />
        <Route path="/admin/organizations" element={<ProtectedRoute><Administration /></ProtectedRoute>} />
        <Route path="/rent-increase" element={<ProtectedRoute><RentIncreasePage /></ProtectedRoute>} />
        <Route path="/rent-increase/:caseId" element={<ProtectedRoute><Navigate to="/rent-increase" replace /></ProtectedRoute>} />
        <Route path="/mietspiegel" element={<ProtectedRoute><Navigate to="/rent-increase" replace /></ProtectedRoute>} />
        <Route path="/inbox" element={<ProtectedRoute><Inbox /></ProtectedRoute>} />
        <Route path="/sales" element={<ProtectedRoute><Sales /></ProtectedRoute>} />
        <Route path="/sales/new" element={<ProtectedRoute><SalesListingForm /></ProtectedRoute>} />
        <Route path="/sales/:id" element={<ProtectedRoute><SalesListingDetail /></ProtectedRoute>} />
        <Route path="/sales/:id/edit" element={<ProtectedRoute><SalesListingForm /></ProtectedRoute>} />
        <Route path="/invoices" element={<ProtectedRoute><Invoices /></ProtectedRoute>} />
        <Route path="/invoices/settings" element={<ProtectedRoute><InvoiceSettings /></ProtectedRoute>} />
        <Route path="/invoices/new" element={<ProtectedRoute><InvoiceEditor /></ProtectedRoute>} />
        <Route path="/invoices/:id" element={<ProtectedRoute><InvoiceDetail /></ProtectedRoute>} />
        <Route path="/invoices/:id/edit" element={<ProtectedRoute><InvoiceEditor /></ProtectedRoute>} />
        
        {/* Onboarding */}
        <Route path="/onboarding" element={<ProtectedRoute><OnboardingDashboard /></ProtectedRoute>} />
        <Route path="/onboarding/new" element={<ProtectedRoute><OnboardingWizardPage /></ProtectedRoute>} />
        <Route path="/onboarding/templates" element={<ProtectedRoute><OnboardingTemplates /></ProtectedRoute>} />
        <Route path="/onboarding/cases/:caseId" element={<ProtectedRoute><OnboardingDetail /></ProtectedRoute>} />
        
        {/* Legal pages */}
        <Route path="/impressum" element={<Impressum />} />
        <Route path="/datenschutz" element={<Datenschutz />} />
        
        {/* Owner Portal (external owner accounts) */}
        <Route path="/owner-portal/accept/:token" element={<PublicRoute><OwnerPortalAcceptInvitation /></PublicRoute>} />
        <Route path="/owner-portal" element={<PortalProtectedRoute><OwnerPortalDashboard /></PortalProtectedRoute>} />
        <Route path="/owner-portal/apartments/:id" element={<PortalProtectedRoute><OwnerPortalApartment /></PortalProtectedRoute>} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}

function App() {
  return (
    <ThemeProvider>
      <BrandingProvider>
        <AuthProvider>
          <OrganizationProvider>
            <FeatureFlagsProvider>
              <BrowserRouter>
                <AppRoutes />
                <UpgradeModal />
                <Toaster 
                  position="top-right" 
                  richColors 
                  closeButton
                  toastOptions={{
                    duration: 4000,
                  }}
                />
              </BrowserRouter>
            </FeatureFlagsProvider>
          </OrganizationProvider>
        </AuthProvider>
      </BrandingProvider>
    </ThemeProvider>
  );
}

export default App;
